import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Field, Input, ListRow, Screen, Stack } from '../components';
import { ChevronLeftIcon, UsersIcon } from '../components/icons';
import { IconTile } from '../components';
import { joinTrip, previewTrip } from '../data/trips';
import type { TripPreview } from '../data/types';
import { useSubmit } from '../hooks/useSubmit';

/**
 * Joining is two steps on purpose.
 *
 * The contract: "If an unclaimed seat matches the caller's name, offer it;
 * otherwise create a new seat. Response lists unclaimed seats so the client
 * can ask 'are you one of these?'" — and offering is not the same as guessing.
 * Two people called Sam on the same trip is not a rare event, and silently
 * binding the wrong one to a seat attaches somebody else's dinners to your
 * balance with nothing on screen to say it happened.
 *
 * So: look up the code, show what is there, let the person point at themselves.
 */
export function JoinTripPage() {
  const navigate = useNavigate();
  const lookup = useSubmit();
  const join = useSubmit();

  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<TripPreview | null>(null);
  const [chosenSeat, setChosenSeat] = useState<string | null>(null);

  async function handleLookup(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;

    let found: TripPreview | null = null;
    const ok = await lookup.run(async () => {
      found = await previewTrip(trimmed);
    });

    if (!ok || !found) return;

    const result = found as TripPreview;

    // Already on this trip: nothing to choose, just go.
    if (result.already_member) {
      navigate(`/trips/${result.trip_id}`, { replace: true });
      return;
    }

    setPreview(result);
    setChosenSeat(result.suggested_member_id);
  }

  async function handleJoin() {
    if (!preview) return;
    const ok = await join.run(() => joinTrip(code.trim().toUpperCase(), chosenSeat));
    if (ok) navigate(`/trips/${preview.trip_id}`, { replace: true });
  }

  return (
    <Screen maxWidth={460}>
      <Stack gap={5}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)' }}>
          <ChevronLeftIcon size={16} />
          Your trips
        </Link>

        <h1 style={{ fontSize: 'var(--fs-h1)' }}>Join a trip</h1>

        <Card>
          <form onSubmit={handleLookup} noValidate>
            <Stack gap={4}>
              {lookup.error && <Alert tone="error">{lookup.error}</Alert>}

              <Field label="Invite code" hint="Six characters, from whoever set the trip up.">
                {({ id }) => (
                  <Input
                    id={id}
                    autoFocus
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.toUpperCase());
                      setPreview(null);
                    }}
                    placeholder="A1B2C3"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={12}
                    style={{ letterSpacing: '0.16em', fontFamily: 'var(--font-display)' }}
                  />
                )}
              </Field>

              {!preview && (
                <Button type="submit" size="lg" fullWidth loading={lookup.submitting} disabled={!code.trim()}>
                  Find trip
                </Button>
              )}
            </Stack>
          </form>
        </Card>

        {preview && (
          <Card>
            <Stack gap={4}>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>Joining</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-h2)', fontWeight: 600 }}>
                  {preview.name}
                </div>
              </div>

              {join.error && <Alert tone="error">{join.error}</Alert>}

              {preview.unclaimed_seats.length > 0 && (
                <div>
                  <div style={{ fontSize: 'var(--fs-body-md)', color: 'var(--text-secondary)', marginBottom: 'var(--sp-2)' }}>
                    Are you one of these?
                  </div>
                  <Card flush>
                    {preview.unclaimed_seats.map((seat, index) => (
                      <ListRow
                        key={seat.id}
                        divider={index > 0}
                        onClick={() => setChosenSeat(seat.id)}
                        style={{
                          background: chosenSeat === seat.id ? 'var(--accent-subtle)' : undefined,
                        }}
                      >
                        <IconTile size={36} background="var(--accent-subtle)">
                          <UsersIcon size={17} />
                        </IconTile>
                        <span style={{ flex: 1, fontSize: 'var(--fs-body-lg)' }}>{seat.display_name}</span>
                        <input
                          type="radio"
                          name="seat"
                          readOnly
                          checked={chosenSeat === seat.id}
                          aria-label={`I am ${seat.display_name}`}
                          style={{ accentColor: 'var(--accent)', width: 18, height: 18 }}
                        />
                      </ListRow>
                    ))}
                    <ListRow
                      divider
                      onClick={() => setChosenSeat(null)}
                      style={{ background: chosenSeat === null ? 'var(--accent-subtle)' : undefined }}
                    >
                      <IconTile size={36} background="var(--accent-subtle)">
                        <UsersIcon size={17} />
                      </IconTile>
                      <span style={{ flex: 1, fontSize: 'var(--fs-body-lg)' }}>
                        None of these — add me as {preview.your_name ?? 'a new person'}
                      </span>
                      <input
                        type="radio"
                        name="seat"
                        readOnly
                        checked={chosenSeat === null}
                        aria-label="Add me as a new person"
                        style={{ accentColor: 'var(--accent)', width: 18, height: 18 }}
                      />
                    </ListRow>
                  </Card>
                  {preview.suggested_member_id && chosenSeat === preview.suggested_member_id && (
                    <p style={{ marginTop: 'var(--sp-2)', color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
                      Picked for you because the name matches yours. Change it if that is not you —
                      claiming a seat takes on everything already logged against it.
                    </p>
                  )}
                </div>
              )}

              <Button size="lg" fullWidth onClick={() => void handleJoin()} loading={join.submitting}>
                {chosenSeat ? 'Claim this seat' : 'Join the trip'}
              </Button>
            </Stack>
          </Card>
        )}
      </Stack>
    </Screen>
  );
}
